import { useEffect, useState, type RefObject } from 'react';
import { Portal, Select, SelectValueChangeDetails, createListCollection } from '@chakra-ui/react';
import { useMap } from '@/components/map/map-context';
import { layers } from '@/geospatial';
import TileWMS from 'ol/source/TileWMS';
import { storageManager } from '@/lib/utils/storage-manager';

const themes = createListCollection({
  items: [
    {
      label: 'Soil Zone',
      value: 'soil_zone_sld',
    },
    {
      label: 'Map Unit',
      value: 'assoctheme',
    },
    {
      label: 'Dominant Ag. Capability',
      value: 'dom_ag_cap_sld',
    },
    {
      label: 'Dominant Texture',
      value: 'dominant_texture_style',
    },
    {
      label: 'Salinity Effect on Production',
      value: 'salsym_style_css',
    },
    {
      label: 'pH',
      value: 'phsym_style_css',
    },
    {
      label: 'Initial Irrigation Potential',
      value: 'initilal_irrig_pooled',
    },
  ],
});

export const ThemeSelection = ({ portalRef }: { portalRef?: RefObject<HTMLDivElement | null> }) => {
  const { map, setCurrentTheme, setLegendVisible } = useMap();
  const [selectedTheme, setSelectedTheme] = useState<string>(() => {
    return storageManager.loadTheme();
  });

  useEffect(() => {
    if (map) {
      const layer = layers[0].layer;
      const style = selectedTheme === 'phsym_style_css' ? 'cphsym_sld' : selectedTheme;
      
      if ('getSource' in layer) {
        const source = layer.getSource();
        if (source instanceof TileWMS) {
          source.updateParams({ STYLES: style });
        }
      }
    }
    setCurrentTheme(selectedTheme);
  }, [map, selectedTheme, setCurrentTheme]);

  // Handle legend visibility based solely on selected theme (do not toggle on map loading/zoom)
  useEffect(() => {
    setLegendVisible(selectedTheme !== 'assoctheme');
  }, [selectedTheme, setLegendVisible]);

  const handleChange = (details: SelectValueChangeDetails<{ label: string; value: string }>) => {
    if (details?.value && details.value.length > 0) {
      const newTheme = details.value[0];
      setSelectedTheme(newTheme);
      setCurrentTheme(newTheme);
      storageManager.saveTheme(newTheme);
    }
  };

  return (
    <Select.Root
      size='sm'
      width='full'
      collection={themes}
      defaultValue={[selectedTheme]}
      onValueChange={handleChange}
    >
      <Select.HiddenSelect />
      <Select.Label>Theme:</Select.Label>
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder='Select theme' />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal container={portalRef}>
        <Select.Positioner style={{ zIndex: 1500 }}>
          <Select.Content bg='white'>
            {themes.items.map((theme) => (
              <Select.Item item={theme} key={theme.value}>
                {theme.label}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
};
